'use client';

import { useState, useEffect } from 'react';

interface WordTiming {
  w: string;
  start: number;
  end: number;
}

interface WordByWordCaptionProps {
  wordTimings: WordTiming[];
  currentTime: number;
  isPlaying: boolean;
  isLastPage?: boolean;
}

export default function WordByWordCaption({ 
  wordTimings, 
  currentTime,
  isPlaying,
  isLastPage = false
}: WordByWordCaptionProps) {
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [wasPlayingRef, setWasPlaying] = useState(false);

  // 🎯 Fixed Window Configuration - Active word ALWAYS in center (like subtitles)
  const WORDS_BEFORE = 3;  // Show 3 words before current
  const WORDS_AFTER = 3;   // Show 3 words after current
  const TOTAL_VISIBLE = WORDS_BEFORE + 1 + WORDS_AFTER; // Total: 7 words

  // When playback starts from beginning, immediately activate first word
  useEffect(() => {
    if (isPlaying && !wasPlayingRef && currentTime < 0.1 && wordTimings && wordTimings.length > 0) {
      console.log('[WordCaption] 🎬 Playback just started - activating first word');
      setCurrentWordIndex(0);
    }
    setWasPlaying(isPlaying);
  }, [isPlaying, wasPlayingRef, currentTime, wordTimings]);

  useEffect(() => {
    if (!wordTimings || wordTimings.length === 0) {
      setCurrentWordIndex(-1);
      return;
    }

    // Find the active word based on current time
    // Use small tolerance (10ms) for timing precision
    const TIME_TOLERANCE = 0.01;
    const activeIndex = wordTimings.findIndex((timing, idx) => {
      const isInRange = (currentTime + TIME_TOLERANCE) >= timing.start && 
        (idx === wordTimings.length - 1 || currentTime < wordTimings[idx + 1].start);
      return isInRange;
    });

    if (activeIndex !== -1 && activeIndex !== currentWordIndex) {
      console.log('[WordCaption] Setting current word to index', activeIndex, '- word:', wordTimings[activeIndex].w, 'at time', currentTime.toFixed(2));
      setCurrentWordIndex(activeIndex);
    } else if (activeIndex === -1 && currentTime === 0 && !isPlaying) {
      // Only reset to preview when NOT playing
      console.log('[WordCaption] Resetting to preview mode - currentTime: 0, isPlaying:', isPlaying);
      setCurrentWordIndex(-1);
    } else if (activeIndex === -1 && isPlaying) {
      // If playing but no word found, check if we're before first word
      if (currentTime < wordTimings[0].start) {
        console.log('[WordCaption] Playing but before first word - staying in preview');
        setCurrentWordIndex(-1);
      } else {
        // We're past words, find closest
        console.log('[WordCaption] Playing but no exact match - currentTime:', currentTime);
      }
    }
  }, [currentTime, wordTimings, isPlaying, currentWordIndex]);

  // Calculate visible word range with CENTER-ALIGNED window
  const getVisibleWords = () => {
    if (currentWordIndex === -1) {
      // Show first 7 words as preview before playback starts
      return wordTimings.slice(0, Math.min(TOTAL_VISIBLE, wordTimings.length)).map((timing, idx) => ({
        ...timing,
        originalIndex: idx,
        position: 'preview' as const
      }));
    }
    
    // Center the current word - calculate start and end indices
    const startIndex = Math.max(0, currentWordIndex - WORDS_BEFORE);
    const endIndex = Math.min(wordTimings.length, currentWordIndex + WORDS_AFTER + 1);
    
    // Map words with their positions relative to center
    return wordTimings.slice(startIndex, endIndex).map((timing, idx) => {
      const originalIndex = startIndex + idx;
      const relativePosition = originalIndex - currentWordIndex;
      
      return {
        ...timing,
        originalIndex,
        relativePosition, // -3, -2, -1, 0 (center), 1, 2, 3
        position: relativePosition === 0 ? 'center' as const : 
                  relativePosition < 0 ? 'before' as const : 
                  'after' as const
      };
    });
  };

  const visibleWords = getVisibleWords();

  if (!wordTimings || wordTimings.length === 0) {
    // On last page, don't show "Ready to play..." - modal will appear after narration
    if (isLastPage) {
      return (
        <div className="caption-placeholder">
          <style jsx>{`
            .caption-placeholder {
              width: 100%;
              text-align: center;
              padding: 1.5rem;
              min-height: 120px;
              display: flex;
              align-items: center;
              justify-content: center;
            }
          `}</style>
        </div>
      );
    }
    
    return (
      <div className="caption-placeholder">
        <p className="caption-text placeholder">Ready to play...</p>
        <style jsx>{`
          .caption-placeholder {
            width: 100%;
            text-align: center;
            padding: 1.5rem;
            min-height: 120px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .caption-text {
            margin: 0;
            font-size: 1.125rem;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.5);
            text-align: center;
            line-height: 1.5;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          }
          .placeholder {
            font-style: italic;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="caption-container">
      <div className="caption-line">
        {visibleWords.map((word) => {
          const isCenter = word.position === 'center';
          const isBefore = word.position === 'before';
          const isAfter = word.position === 'after';
          const isPreview = word.position === 'preview';
          
          return (
            <span 
              key={`word-${word.originalIndex}`}
              className={`word-wrapper ${isCenter ? 'center-word' : ''} ${isPreview ? 'preview-word' : ''}`}
            >
              <span
                className={`word-span ${isCenter ? 'active' : isBefore ? 'past' : 'future'}`}
              >
                {word.w}
              </span>
            </span>
          );
        })}
      </div>
      <style jsx>{`
        .caption-container {
          width: 100%;
          padding: 1.5rem;
          min-height: 120px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          position: relative;
        }

        /* Fixed caption line - words flow horizontally, centered */
        .caption-line {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          max-width: 100%;
          flex-wrap: nowrap;
          position: relative;
        }

        /* Each word wrapper maintains fixed spacing */
        .word-wrapper {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Preview words (before playback starts) */
        .word-wrapper.preview-word .word-span {
          color: rgba(255, 255, 255, 0.85);
          font-weight: 500;
          font-size: 1.3rem;
          padding: 4px 8px;
          transform: scale(1);
        }

        /* Center word emphasis - ALWAYS in middle */
        .word-wrapper.center-word {
          z-index: 10;
        }

        .word-span {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          display: inline-block;
          padding: 6px 10px;
          border-radius: 8px;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          white-space: nowrap;
        }

        /* 🌟 Active word - CENTER position (like professional subtitles) */
        .word-span.active {
          font-size: 1.6rem;
          font-weight: 900;
          color: #1F2937;
          background: linear-gradient(135deg, 
            #FEF08A 0%, 
            #FDE047 50%, 
            #FACC15 100%);
          text-shadow: 0 3px 12px rgba(250, 204, 21, 0.6),
                       0 0 30px rgba(254, 240, 138, 0.8);
          box-shadow: 0 8px 20px rgba(250, 204, 21, 0.5),
                      0 0 0 4px rgba(254, 240, 138, 0.7),
                      inset 0 2px 0 rgba(255, 255, 255, 0.7);
          transform: scale(1.25);
          padding: 8px 14px;
          animation: spotlightPulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Past words - fade out gracefully */
        .word-span.past {
          font-size: 1.2rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.65);
          background: linear-gradient(135deg, 
            rgba(254, 240, 138, 0.2) 0%, 
            rgba(253, 224, 71, 0.15) 100%);
          text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
          transform: scale(0.95);
          opacity: 0.8;
          padding: 5px 9px;
        }

        /* Future words - subtle preview */
        .word-span.future {
          font-size: 1.2rem;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.75);
          background: transparent;
          text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
          transform: scale(0.95);
          opacity: 0.9;
          padding: 5px 9px;
        }

        /* 🎯 Spotlight animation for center word */
        @keyframes spotlightPulse {
          0% {
            transform: scale(0.95);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.3);
            opacity: 1;
          }
          100% {
            transform: scale(1.25);
            opacity: 1;
          }
        }

        /* 📱 Mobile optimization - smaller screens */
        @media (max-width: 480px) {
          .caption-container {
            padding: 1.25rem 1rem;
            min-height: 110px;
          }

          .caption-line {
            gap: 0.4rem;
          }

          .word-span.active {
            font-size: 1.4rem;
            padding: 7px 12px;
            transform: scale(1.2);
          }

          .word-span.past,
          .word-span.future {
            font-size: 1.1rem;
            padding: 4px 8px;
          }

          .word-wrapper.preview-word .word-span {
            font-size: 1.2rem;
          }
        }

        /* 📱 Large phones */
        @media (min-width: 481px) and (max-width: 767px) {
          .word-span.active {
            font-size: 1.7rem;
          }

          .word-span.past,
          .word-span.future {
            font-size: 1.25rem;
          }
        }

        /* ♿ Accessibility: Reduced Motion */
        @media (prefers-reduced-motion: reduce) {
          .word-span {
            transition: opacity 0.2s ease, color 0.2s ease !important;
            animation: none !important;
            transform: none !important;
          }
          
          .word-span.active {
            transform: scale(1.1) !important;
          }
          
          @keyframes spotlightPulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1.1);
            }
          }
        }

        /* ♿ Accessibility: High Contrast */
        @media (prefers-contrast: high) {
          .word-span {
            color: #FFFFFF !important;
          }
          
          .word-span.active {
            color: #000000 !important;
            background: #FFFF00 !important;
            border: 2px solid #000000;
          }
        }

        /* 🎨 Performance optimization */
        .word-span {
          will-change: transform, opacity;
        }

        .caption-line {
          will-change: contents;
        }
      `}</style>
    </div>
  );
}
